import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Filter, Save, X, ChevronDown, ChevronUp, Users, RefreshCw } from 'lucide-react';
import Button from '../../../ui-kit/Button';
import { CustomerService } from '../services/CustomerService';
import { authHeaders } from '../../../core/api/authHeaders';

const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

/**
 * Every filter the audience builder understands, with its cleared value. One list,
 * so "clear all", the URL sync and the saved presets can never disagree about what a
 * filter is or what empty looks like.
 */
export const FILTER_DEFAULTS = {
    // Customer profile
    search: '', city: '', riskLevel: 'all', healthScore: 'all', customerTag: 'all',
    // Purchase history
    minSpent: '', maxSpent: '', orderCount: 'all', minOrders: '', maxOrders: '',
    buyerType: 'all', minSuccessOrders: '', minSuccessSpend: '', minAvgOrderValue: '',
    // Inactivity & retention — the win-back controls
    lastSuccess: 'all', successFrom: '', successTo: '', segment: 'all', successEvidence: 'all',
    // Products
    boughtProduct: '', neverBoughtProduct: '', notReorderedProduct: '', notReorderedDays: '365',
    // Delivery & complaints
    minRto: '', neverDelivered: '', historyIncomplete: '', hasComplaint: 'all', doNotShip: 'all',
    // Contact eligibility
    callsMade: 'all',
    // Legacy last-ORDER filters, kept so old saved presets keep working
    dateRange: 'all', lastFrom: '', lastTo: '',
};

const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-100';
const labelCls = 'block text-xs font-medium text-slate-500 mb-1';

const Field = ({ label, children, span = 1 }) => (
    <div className={span === 2 ? 'col-span-1 md:col-span-2' : ''}>
        <label className={labelCls}>{label}</label>
        {children}
    </div>
);

/** One collapsible group. Open by default when it already holds a value. */
const Section = ({ title, hint, count, children, defaultOpen }) => {
    const [open, setOpen] = useState(!!defaultOpen);
    useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);
    return (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                <span className="text-sm font-semibold text-slate-700">
                    {title}
                    {count > 0 && <span className="ml-2 text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full px-1.5 py-0.5">{count}</span>}
                </span>
                {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            {open && (
                <div className="p-3 space-y-3">
                    {hint && <p className="text-[11px] text-slate-400 leading-relaxed">{hint}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">{children}</div>
                </div>
            )}
        </div>
    );
};

/**
 * Audience builder for Customer Intelligence.
 *
 * The filters that matter for a win-back run on the LAST SUCCESSFUL PURCHASE, not the
 * last order: a 2026 order that was cancelled or came back RTO must not make a 2024
 * customer look active. All of it is evaluated on the server against the compact
 * customer index, so the browser never downloads customer records to filter them.
 */
const AdvancedFilterPanel = ({ initialFilters, onApplyFilters, userId = 'admin' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filters, setFilters] = useState({ ...FILTER_DEFAULTS });
    const [families, setFamilies] = useState([]);
    const [preview, setPreview] = useState({ total: null, loading: false });
    const previewReq = useRef(0);

    useEffect(() => {
        if (initialFilters && Object.keys(initialFilters).length) {
            setFilters(f => ({ ...f, ...initialFilters }));
            setIsOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Product families come from the server so this dropdown cannot drift from the
    // mapping that actually classifies orders.
    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${PROXY}/api/customers/product-families`, { headers: await authHeaders() });
                const j = await r.json();
                if (Array.isArray(j.families)) setFamilies(j.families);
            } catch { /* the product filters simply stay empty */ }
        })();
    }, []);

    const [savedPresets, setSavedPresets] = useState({});
    const [presetName, setPresetName] = useState('');
    useEffect(() => { loadPresets(); }, []);          // eslint-disable-line react-hooks/exhaustive-deps

    const loadPresets = async () => setSavedPresets(await CustomerService.getFilterPresets(userId));
    const handleSavePreset = async () => {
        if (!presetName) return;
        // The WHOLE filter set is saved, so a preset restores exactly what it matched.
        await CustomerService.saveFilterPreset(userId, presetName, { ...FILTER_DEFAULTS, ...filters });
        setPresetName('');
        loadPresets();
    };
    const loadPreset = (name) => {
        if (savedPresets[name]) setFilters({ ...FILTER_DEFAULTS, ...savedPresets[name] });
    };

    const handleChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
    const apply = () => onApplyFilters(filters);
    const clear = () => { setFilters({ ...FILTER_DEFAULTS }); onApplyFilters({ ...FILTER_DEFAULTS }); };

    /** Everything the user has actually set, for the URL and for counting. */
    const activeEntries = useMemo(() => Object.entries(filters)
        .filter(([k, v]) => v !== undefined && v !== null && v !== '' && v !== 'all' && FILTER_DEFAULTS[k] !== v), [filters]);
    const activeCount = activeEntries.length;
    const countIn = (keys) => activeEntries.filter(([k]) => keys.includes(k)).length;

    // Live audience size, BEFORE committing the selection. Debounced, and the newest
    // request always wins so a slow earlier one cannot overwrite it.
    useEffect(() => {
        if (!isOpen) return undefined;
        const mine = ++previewReq.current;
        setPreview(p => ({ ...p, loading: true }));
        const t = setTimeout(async () => {
            try {
                const q = new URLSearchParams();
                activeEntries.forEach(([k, v]) => q.set(k, String(v)));
                q.set('limit', '1');
                const r = await fetch(`${PROXY}/api/customers/query?${q}`, { headers: await authHeaders() });
                const j = await r.json();
                if (mine !== previewReq.current) return;
                setPreview({ total: Number(j.total) || 0, loading: false });
            } catch {
                if (mine === previewReq.current) setPreview({ total: null, loading: false });
            }
        }, 350);
        return () => clearTimeout(t);
    }, [activeEntries, isOpen]);

    const productOptions = (
        <>
            <option value="">Any product</option>
            {families.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </>
    );

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 mb-6">
            <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors rounded-xl"
                onClick={() => setIsOpen(!isOpen)}>
                <div className="flex items-center gap-2 text-slate-700 font-medium">
                    <Filter size={18} className="text-purple-600" />
                    Advanced Filters
                    {activeCount > 0 && (
                        <span className="text-[10px] font-bold bg-purple-600 text-white rounded-full px-2 py-0.5">{activeCount} active</span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {activeCount > 0 && preview.total !== null && (
                        <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">
                            <Users size={13} /> {preview.total.toLocaleString('en-IN')} match
                        </span>
                    )}
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
            </div>

            {isOpen && (
                <div className="p-4 border-t border-slate-100 space-y-3">
                    {Object.keys(savedPresets).length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                            {Object.keys(savedPresets).map(name => (
                                <button key={name} onClick={() => loadPreset(name)}
                                    className="px-3 py-1 text-xs bg-purple-50 text-purple-700 rounded-full border border-purple-100 hover:bg-purple-100 transition-colors">
                                    {name}
                                </button>
                            ))}
                        </div>
                    )}

                    <Section title="Customer profile" defaultOpen count={countIn(['search', 'city', 'riskLevel', 'healthScore', 'customerTag'])}>
                        <Field label="Search" span={2}>
                            <input type="text" placeholder="Name or mobile…" className={inputCls}
                                value={filters.search} onChange={e => handleChange('search', e.target.value)} />
                        </Field>
                        <Field label="City">
                            <input type="text" placeholder="e.g. Thrissur" className={inputCls}
                                value={filters.city} onChange={e => handleChange('city', e.target.value)} />
                        </Field>
                        <Field label="Churn risk">
                            <select className={inputCls} value={filters.riskLevel} onChange={e => handleChange('riskLevel', e.target.value)}>
                                <option value="all">Any</option><option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option>
                            </select>
                        </Field>
                        <Field label="Health score">
                            <select className={inputCls} value={filters.healthScore} onChange={e => handleChange('healthScore', e.target.value)}>
                                <option value="all">Any</option><option value="excellent">Excellent (80+)</option><option value="good">Good (51-80)</option><option value="poor">Poor (50 or less)</option>
                            </select>
                        </Field>
                        <Field label="Customer list">
                            <select className={inputCls} value={filters.customerTag} onChange={e => handleChange('customerTag', e.target.value)}>
                                <option value="all">All customers</option>
                                <option value="TN customer">TN customers only</option>
                                <option value="none">Exclude TN customers</option>
                            </select>
                        </Field>
                    </Section>

                    <Section
                        title="Inactivity & retention"
                        defaultOpen={countIn(['lastSuccess', 'segment', 'successEvidence', 'successFrom', 'successTo']) > 0}
                        count={countIn(['lastSuccess', 'segment', 'successEvidence', 'successFrom', 'successTo'])}
                        hint="Measured on the last SUCCESSFUL purchase — a cancelled order or a parcel that came back RTO never counts as one, so a customer who ordered recently and did not receive it still reads as inactive.">
                        <Field label="Last successful purchase">
                            <select className={inputCls} value={filters.lastSuccess} onChange={e => handleChange('lastSuccess', e.target.value)}>
                                <option value="all">Any time</option>
                                <option value="within30">Within last 30 days</option>
                                <option value="days6_30">6-30 days ago (skips last 6 days)</option>
                                <option value="days31_60">31-60 days ago</option>
                                <option value="days61_90">61-90 days ago</option>
                                <option value="days91_180">91-180 days ago</option>
                                <option value="days181_365">181-365 days ago</option>
                                <option value="over365">More than 365 days ago</option>
                                <option value="over540">More than 540 days ago</option>
                                <option value="over730">More than 730 days ago</option>
                                <option value="never">Never successfully purchased</option>
                                <option value="custom">Custom date range…</option>
                            </select>
                        </Field>
                        {filters.lastSuccess === 'custom' && (
                            <>
                                <Field label="Purchased from">
                                    <input type="date" className={inputCls} value={filters.successFrom} onChange={e => handleChange('successFrom', e.target.value)} />
                                </Field>
                                <Field label="Purchased to">
                                    <input type="date" className={inputCls} value={filters.successTo} onChange={e => handleChange('successTo', e.target.value)} />
                                </Field>
                            </>
                        )}
                        <Field label="Lifecycle segment">
                            <select className={inputCls} value={filters.segment} onChange={e => handleChange('segment', e.target.value)}>
                                <option value="all">Any</option>
                                <option value="new">New — never purchased</option>
                                <option value="active">Active (within 90 days)</option>
                                <option value="at_risk">At risk (91-180)</option>
                                <option value="dormant">Dormant (181-365)</option>
                                <option value="lost">Lost (365+)</option>
                            </select>
                        </Field>
                        <Field label="Delivery evidence">
                            <select className={inputCls} value={filters.successEvidence} onChange={e => handleChange('successEvidence', e.target.value)}>
                                <option value="all">Any evidence</option>
                                <option value="scan">Courier scan only</option>
                                <option value="attested">Attested (no scan)</option>
                            </select>
                        </Field>
                    </Section>

                    <Section
                        title="Products purchased"
                        defaultOpen={countIn(['boughtProduct', 'neverBoughtProduct', 'notReorderedProduct']) > 0}
                        count={countIn(['boughtProduct', 'neverBoughtProduct', 'notReorderedProduct'])}
                        hint="Pack sizes are the same product: Anjenna Plus Pack of 5 and Pack of 12 both count as Anjenna Plus. A product bought inside a combo counts too. A parcel that came back RTO does not count as having bought it.">
                        <Field label="Bought this product">
                            <select className={inputCls} value={filters.boughtProduct} onChange={e => handleChange('boughtProduct', e.target.value)}>{productOptions}</select>
                        </Field>
                        <Field label="Never bought">
                            <select className={inputCls} value={filters.neverBoughtProduct} onChange={e => handleChange('neverBoughtProduct', e.target.value)}>{productOptions}</select>
                        </Field>
                        <Field label="Bought but not reordered">
                            <select className={inputCls} value={filters.notReorderedProduct} onChange={e => handleChange('notReorderedProduct', e.target.value)}>{productOptions}</select>
                        </Field>
                        {filters.notReorderedProduct && (
                            <Field label="Not reordered for (days)">
                                <input type="number" min="1" className={inputCls} value={filters.notReorderedDays}
                                    onChange={e => handleChange('notReorderedDays', e.target.value)} />
                            </Field>
                        )}
                    </Section>

                    <Section title="Purchase history" count={countIn(['buyerType', 'minSuccessOrders', 'minSuccessSpend', 'minAvgOrderValue', 'minSpent', 'maxSpent', 'minOrders', 'maxOrders', 'orderCount'])}>
                        <Field label="Buyer type">
                            <select className={inputCls} value={filters.buyerType} onChange={e => handleChange('buyerType', e.target.value)}>
                                <option value="all">Any</option>
                                <option value="one_time">One-time buyers</option>
                                <option value="repeat">Repeat buyers</option>
                            </select>
                        </Field>
                        <Field label="Min successful orders">
                            <input type="number" min="0" className={inputCls} value={filters.minSuccessOrders} onChange={e => handleChange('minSuccessOrders', e.target.value)} />
                        </Field>
                        <Field label="Min successful spend (₹)">
                            <input type="number" min="0" className={inputCls} value={filters.minSuccessSpend} onChange={e => handleChange('minSuccessSpend', e.target.value)} />
                        </Field>
                        <Field label="Min average order (₹)">
                            <input type="number" min="0" className={inputCls} value={filters.minAvgOrderValue} onChange={e => handleChange('minAvgOrderValue', e.target.value)} />
                        </Field>
                        <Field label="Lifetime spend from (₹)">
                            <input type="number" min="0" className={inputCls} value={filters.minSpent} onChange={e => handleChange('minSpent', e.target.value)} />
                        </Field>
                        <Field label="Lifetime spend to (₹)">
                            <input type="number" min="0" className={inputCls} value={filters.maxSpent} onChange={e => handleChange('maxSpent', e.target.value)} />
                        </Field>
                        <Field label="Orders from">
                            <input type="number" min="0" className={inputCls} value={filters.minOrders} onChange={e => handleChange('minOrders', e.target.value)} />
                        </Field>
                        <Field label="Orders to">
                            <input type="number" min="0" className={inputCls} value={filters.maxOrders} onChange={e => handleChange('maxOrders', e.target.value)} />
                        </Field>
                    </Section>

                    <Section title="Delivery & complaints" count={countIn(['minRto', 'neverDelivered', 'historyIncomplete', 'hasComplaint', 'doNotShip'])}>
                        <Field label="Minimum RTO count">
                            <input type="number" min="0" className={inputCls} value={filters.minRto} onChange={e => handleChange('minRto', e.target.value)} />
                        </Field>
                        <Field label="Never received anything">
                            <select className={inputCls} value={filters.neverDelivered} onChange={e => handleChange('neverDelivered', e.target.value)}>
                                <option value="">Any</option><option value="1">Ordered, nothing arrived</option>
                            </select>
                        </Field>
                        <Field label="Outcome unknown">
                            <select className={inputCls} value={filters.historyIncomplete} onChange={e => handleChange('historyIncomplete', e.target.value)}>
                                <option value="">Any</option><option value="1">Has orders, no known outcome</option>
                            </select>
                        </Field>
                        <Field label="Complaints">
                            <select className={inputCls} value={filters.hasComplaint} onChange={e => handleChange('hasComplaint', e.target.value)}>
                                <option value="all">Any</option><option value="open">Has an open ticket</option>
                            </select>
                        </Field>
                        <Field label="Do not ship">
                            <select className={inputCls} value={filters.doNotShip} onChange={e => handleChange('doNotShip', e.target.value)}>
                                <option value="all">Any</option><option value="flagged">Flagged</option>
                            </select>
                        </Field>
                    </Section>

                    <Section title="Contact eligibility" count={countIn(['callsMade'])}
                        hint="Customers marked DND or Do Not Ship are shown with their badge; campaign downloads exclude them.">
                        <Field label="Calls made">
                            <select className={inputCls} value={filters.callsMade} onChange={e => handleChange('callsMade', e.target.value)}>
                                <option value="all">Any</option><option value="never">Never called</option><option value="1-3">1-3 calls</option><option value="4plus">4 or more</option>
                            </select>
                        </Field>
                    </Section>

                    {/* Audience preview + actions */}
                    <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                        <div className="text-sm">
                            {preview.loading ? (
                                <span className="text-slate-400 flex items-center gap-1.5"><RefreshCw size={13} className="animate-spin" /> Counting…</span>
                            ) : preview.total === null ? (
                                <span className="text-slate-400">Audience size unavailable</span>
                            ) : (
                                <span className="font-semibold text-slate-700">
                                    <Users size={14} className="inline mb-0.5 mr-1 text-purple-600" />
                                    {preview.total.toLocaleString('en-IN')} customers match
                                    {activeCount === 0 && <span className="font-normal text-slate-400"> (no filters applied)</span>}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <input type="text" placeholder="Save as preset…" className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-40"
                                value={presetName} onChange={e => setPresetName(e.target.value)} />
                            <Button variant="secondary" icon={Save} onClick={handleSavePreset} disabled={!presetName}>Save</Button>
                            <Button variant="secondary" icon={X} onClick={clear}>Clear all</Button>
                            <Button onClick={apply}>Apply filters</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdvancedFilterPanel;
